import { useEffect, useMemo, useRef, useState } from "react";
import type { ReplyTemplate, Review } from "../types";
import { formatFrenchDay } from "../lib/days";
import { DateField, matchesQuery, SearchField, SelectField } from "./fields";

const PAGE_SIZE = 10;

type Range = "all" | "7" | "30" | "90" | "custom";

type Props = {
  reviews: Review[];
  templates: ReplyTemplate[];
  busyName: string | null;
  onReply: (name: string, comment: string) => Promise<void>;
};

function stars(n: number): string {
  return "★".repeat(Math.max(0, Math.min(5, n))) + "☆".repeat(5 - Math.max(0, Math.min(5, n)));
}

function dayStamp(iso: string): string {
  return iso.slice(0, 10);
}

function sinceDays(days: number): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export default function ReviewsPanel({ reviews, templates, busyName, onReply }: Props) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [range, setRange] = useState<Range>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const listRef = useRef<HTMLDivElement>(null);

  const visible = useMemo(() => {
    let start = "";
    let end = "";
    if (range === "7") start = sinceDays(7);
    if (range === "30") start = sinceDays(30);
    if (range === "90") start = sinceDays(90);
    if (range === "custom") {
      start = from;
      end = to;
    }
    return reviews.filter((r) => {
      const day = dayStamp(r.create_time || "");
      if (!day && range !== "all") return false;
      if (start && day < start) return false;
      if (end && day > end) return false;
      return matchesQuery(`${r.reviewer} ${r.comment} ${r.reply || ""} ${r.place_title || ""}`, query);
    });
  }, [reviews, range, from, to, query]);

  const listKey = reviews.map((r) => r.name).join("|");
  useEffect(() => {
    setPage(1);
  }, [range, from, to, query, listKey]);

  const pages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const start = (current - 1) * PAGE_SIZE;
  const shown = visible.slice(start, start + PAGE_SIZE);

  function go(next: number) {
    setPage(next);
    listRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }

  const chips: { id: Range; label: string }[] = [
    { id: "all", label: "Tous" },
    { id: "7", label: "7 jours" },
    { id: "30", label: "30 jours" },
    { id: "90", label: "3 mois" },
    { id: "custom", label: "Période" },
  ];

  return (
    <div className="space-y-5">
      <div className="pr-card flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex flex-wrap gap-2">
          {chips.map((c) => (
            <button key={c.id} type="button" onClick={() => setRange(c.id)} className={`pr-chip ${range === c.id ? "is-on" : "is-off"}`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex min-w-[220px] flex-1 items-center gap-3 sm:max-w-sm">
          <SearchField value={query} onChange={setQuery} placeholder="Avis, réponse, établissement" />
          <p className="shrink-0 text-sm text-slate-400">{visible.length}</p>
        </div>
      </div>
      {range === "custom" ? (
        <div className="grid max-w-lg grid-cols-2 gap-3">
          <div>
            <p className="mb-1 text-xs text-slate-500">Du</p>
            <DateField value={from} onChange={setFrom} />
          </div>
          <div>
            <p className="mb-1 text-xs text-slate-500">Au</p>
            <DateField value={to} onChange={setTo} />
          </div>
        </div>
      ) : null}

      {visible.length === 0 ? (
        <p className="pr-card px-6 py-16 text-center text-sm text-slate-500">
          {query.trim() ? "Aucun avis pour cette recherche." : "Aucun avis sur cette période."}
        </p>
      ) : (
        <div ref={listRef} className="space-y-5">
          {pages > 1 ? (
            <ReviewPager page={current} pages={pages} from={start + 1} to={start + shown.length} total={visible.length} onPage={go} />
          ) : null}
          {shown.map((r) => (
          <article key={r.name} className="pr-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-base font-semibold">{r.reviewer || "Client Google"}</p>
                {r.place_title ? <p className="mt-0.5 text-xs font-medium text-violet">{r.place_title}</p> : null}
                <p className="mt-1 text-sm text-amber-500">{stars(r.rating)}</p>
              </div>
              <time className="text-xs text-slate-400" dateTime={dayStamp(r.create_time)}>
                {formatFrenchDay(r.create_time)}
              </time>
            </div>
            {r.comment ? <p className="mt-4 text-sm leading-6 text-slate-700">{r.comment}</p> : null}

            {r.reply ? (
              <p className="mt-5 rounded-2xl bg-mist px-4 py-3 text-sm leading-6 text-slate-700">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">Votre réponse</span>
                {r.reply}
              </p>
            ) : (
              <form
                className="mt-5 space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  const comment = (drafts[r.name] || "").trim();
                  if (!comment) return;
                  void onReply(r.name, comment).then(() => {
                    setDrafts((prev) => ({ ...prev, [r.name]: "" }));
                  });
                }}
              >
                {templates.length > 0 ? (
                  <SelectField
                    value={picked[r.name] || ""}
                    onChange={(id) => {
                      setPicked((prev) => ({ ...prev, [r.name]: id }));
                      const tpl = templates.find((t) => t.id === id);
                      setDrafts((prev) => ({ ...prev, [r.name]: tpl ? tpl.text : "" }));
                    }}
                    options={[
                      { value: "", label: "Écrire librement" },
                      ...templates.map((t) => ({ value: t.id, label: t.name })),
                    ]}
                  />
                ) : null}
                <textarea
                  rows={4}
                  value={drafts[r.name] || ""}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [r.name]: e.target.value }))}
                  placeholder="Répondre à cet avis…"
                  className="pr-area"
                />
                <button type="submit" disabled={busyName === r.name} className="pr-btn">
                  {busyName === r.name ? "Envoi…" : "Publier"}
                </button>
              </form>
            )}
          </article>
          ))}
          {pages > 1 ? (
            <ReviewPager page={current} pages={pages} from={start + 1} to={start + shown.length} total={visible.length} onPage={go} />
          ) : null}
        </div>
      )}
    </div>
  );
}

function ReviewPager({
  page,
  pages,
  from,
  to,
  total,
  onPage,
}: {
  page: number;
  pages: number;
  from: number;
  to: number;
  total: number;
  onPage: (page: number) => void;
}) {
  return (
    <nav className="flex flex-wrap items-center justify-between gap-3" aria-label="Pages des avis">
      <button type="button" className="pr-btn-ghost disabled:cursor-default disabled:opacity-40" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Précédent
      </button>
      <p className="text-sm text-slate-500">
        {from}–{to} sur {total}
      </p>
      <button type="button" className="pr-btn-ghost disabled:cursor-default disabled:opacity-40" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Suivant
      </button>
    </nav>
  );
}
